import React, { useEffect, useMemo, useState } from "react"
import styles from "./googleIntegration.module.css"
import Button from "./../../components/Form/Button"
import api from "./../../services/api"
import { openLinkExternal } from "../../utils/utils"
import { toast } from "react-toastify"
import { AiOutlineDisconnect } from "react-icons/ai"
import { MdAccountTree, MdAdsClick, MdCheckCircle, MdDataUsage, MdInfoOutline, MdOpenInNew, MdRefresh, MdSecurity, MdWarningAmber, MdChecklist } from "react-icons/md"
import ImageWithFallback from "./../../components/ImageWithFallback"
import ModalConfirm from "../../components/Modal/ModalConfirm"
import Loading from "./../layouts/Loading"
import BackButton from "../../components/BackButton"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import GoogleBrandIcon from "./GoogleBrandIcon"
import ModalGoogleAdAccountSelection from "./ModalGoogleAdAccountSelection"

const GOOGLE_HELP_URL = "https://support.google.com/accounts/answer/3466521"

const GoogleIntegration = () => {
    const [googleAccounts, setGoogleAccounts] = useState([])
    const [googleAdAccounts, setGoogleAdAccounts] = useState([])
    const [authorizationUrl, setAuthorizationUrl] = useState("")
    const [showDisconnect, setShowDisconnect] = useState(false)
    const [accountToDisconnect, setAccountToDisconnect] = useState({})
    const [isLoading, setIsLoading] = useState(false)
    const [loadingConnection, setLoadingConnection] = useState("")
    const [managingConnection, setManagingConnection] = useState(null)
    const [searchParams] = useSearchParams()
    const navigate = useNavigate()
    const { account_id } = useParams()

    const loadAccounts = async () => {
        const response = await api.get("/integrations/google/accounts")
        setGoogleAccounts(response?.data?.data || [])
    }

    const loadAdAccounts = async () => {
        const response = await api.get("/integrations/google/adAccounts")
        setGoogleAdAccounts(response?.data?.data || [])
    }

    const loadAuthorizationUrl = async () => {
        const response = await api.get("/integrations/google/authorization-url")
        setAuthorizationUrl(response?.data?.data || "")
    }

    useEffect(() => {
        setIsLoading(true)
        Promise.all([loadAccounts(), loadAdAccounts(), loadAuthorizationUrl()])
            .catch(error => toast.error(error?.response?.data?.message || "Não foi possível carregar a integração com o Google."))
            .finally(() => setIsLoading(false))
    }, [])

    useEffect(() => {
        const oauthResult = searchParams.get("google_oauth")
        if (!oauthResult) return
        if (oauthResult === "success") toast.success("Conta Google conectada e permissões confirmadas.")
        if (oauthResult === "denied") toast.info("A conexão foi cancelada. Nenhuma permissão foi concedida.")
        if (oauthResult === "error") toast.error(searchParams.get("message") || "Não foi possível conectar a conta Google.")
        navigate(`/${account_id}/integrations/google`, { replace: true })
    }, [account_id, navigate, searchParams])

    const reconnectRequired = googleAccounts.filter(account => !account.has_required_scopes).length
    const managerAccounts = googleAdAccounts.filter(account => account.manager).length
    const sortedGoogleAccounts = useMemo(() => [...googleAccounts].sort((a, b) => String(a.name || a.connected_by?.name || "").localeCompare(String(b.name || b.connected_by?.name || ""), "pt-BR")), [googleAccounts])
    const sortedAdAccounts = useMemo(() => [...googleAdAccounts].sort((a, b) => String(a.descriptive_name || a.customer_id).localeCompare(String(b.descriptive_name || b.customer_id), "pt-BR")), [googleAdAccounts])

    const startGoogleAuthorization = () => {
        if (!authorizationUrl) return toast.info("A autorização ainda está sendo preparada. Tente novamente.")
        sessionStorage.setItem("google_oauth_account_id", String(account_id))
        openLinkExternal(authorizationUrl, false, "_self")
    }

    const syncAdAccounts = async googleUserAccountId => {
        setLoadingConnection(googleUserAccountId || "all")
        try {
            const response = await api.post("/integrations/google/adAccounts/sync", googleUserAccountId ? { google_user_account_id: googleUserAccountId } : {})
            await loadAdAccounts()
            toast.success(response?.data?.message || "Contas Google Ads sincronizadas.")
        } catch (error) {
            toast.error(error?.response?.data?.message || "Não foi possível sincronizar as contas Google Ads.")
        } finally {
            setLoadingConnection("")
        }
    }

    const disconnectAccount = async googleAccount => {
        setIsLoading(true)
        try {
            const response = await api.post("/integrations/google/disconnect", { user_account_id: googleAccount._id })
            setGoogleAccounts(previous => previous.filter(item => item._id !== googleAccount._id))
            setGoogleAdAccounts(previous => previous.filter(item => String(item.google_user_account_id) !== String(googleAccount._id)))
            setShowDisconnect(false)
            setAccountToDisconnect({})
            toast.success(response?.data?.message || "Conta desconectada.")
        } catch (error) {
            toast.error(error?.response?.data?.message || "Não foi possível desconectar a conta.")
        } finally {
            setIsLoading(false)
        }
    }

    const openDisconnect = googleAccount => {
        setAccountToDisconnect(googleAccount)
        setShowDisconnect(true)
    }

    const accountAdsCount = connectionId => googleAdAccounts.filter(account => String(account.google_user_account_id) === String(connectionId)).length
    const formatDate = value => value ? new Intl.DateTimeFormat("pt-BR", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value)) : "Data não disponível"

    return (
        <div className={styles.container}>
            <div className="page-back-row"><BackButton fallback={`/${account_id}/integrations`} /></div>

            <section className={styles.hero}>
                <div className={styles.heroIcon}><GoogleBrandIcon /></div>
                <div className={styles.heroCopy}>
                    <span className={styles.eyebrow}>Integração oficial via OAuth 2.0</span>
                    <h1>Google Ads</h1>
                    <p>Conecte uma conta Google para consultar suas contas de anúncios e enviar conversões offline com segurança. Você escolhe a conta e confirma as permissões diretamente no Google.</p>
                    <div className={styles.heroTrust}><MdSecurity /><span>A Oktto não recebe nem armazena sua senha do Google.</span></div>
                </div>
                <Button className={styles.googleConnectButton} variant="neutral" fullWidth={false} disabled={!authorizationUrl} onClick={startGoogleAuthorization}>
                    <GoogleBrandIcon /> {googleAccounts.length ? "Conectar outra conta" : "Conectar conta Google"}
                </Button>
            </section>

            <section className={styles.statusGrid} aria-label="Resumo da integração">
                <div><span className={styles.statusIcon}><MdCheckCircle /></span><p><strong>{googleAccounts.length}</strong> conexões Google</p><small>{reconnectRequired ? `${reconnectRequired} precisa(m) renovar permissões` : googleAccounts.length ? "Permissões necessárias confirmadas" : "Nenhuma conta conectada"}</small></div>
                <div><span className={styles.statusIcon}><MdAdsClick /></span><p><strong>{googleAdAccounts.length}</strong> contas Ads</p><small>{managerAccounts ? `${managerAccounts} conta(s) administradora(s)` : "Sincronizadas a partir das conexões"}</small></div>
                <div><span className={styles.statusIcon}><MdDataUsage /></span><p><strong>2</strong> permissões do Google</p><small>Google Ads e Data Manager API</small></div>
            </section>

            <section className={styles.permissionPanel}>
                <div className={styles.sectionHeading}>
                    <div><span className={styles.eyebrow}>Transparência</span><h2>O que será autorizado</h2><p>Solicitamos somente os acessos necessários para os recursos desta integração.</p></div>
                </div>
                <div className={styles.permissionGrid}>
                    <div><MdAdsClick /><section><strong>Gerenciar dados do Google Ads</strong><span>Consultar contas, campanhas e ações de conversão; criar e atualizar ações quando você solicitar.</span></section></div>
                    <div><MdDataUsage /><section><strong>Enviar conversões offline</strong><span>Validar e enviar eventos do CRM para as ações selecionadas por meio da Data Manager API.</span></section></div>
                    <div><MdSecurity /><section><strong>Acesso seguro e revogável</strong><span>O token é protegido e você pode remover o acesso aqui ou na sua Conta Google a qualquer momento.</span></section></div>
                </div>
                <div className={styles.policyLinks}><MdInfoOutline /><span>Veja <a href="https://oktto.com.br/politica-de-privacidade/" target="_blank" rel="noopener noreferrer">como usamos dados do Google</a>, consulte os <a href="https://oktto.com.br/termos-de-servico/" target="_blank" rel="noopener noreferrer">Termos de Serviço</a> ou <a href={GOOGLE_HELP_URL} target="_blank" rel="noopener noreferrer">gerencie o acesso na Conta Google <MdOpenInNew /></a>.</span></div>
            </section>

            <section className={styles.card}>
                <div className={styles.sectionHeading}>
                    <div><span className={styles.eyebrow}>Credenciais</span><h2>Contas Google conectadas</h2><p>Cada conexão deve ter acesso às contas Google Ads que serão sincronizadas.</p></div>
                </div>
                <div className={styles.connectionList}>
                    {sortedGoogleAccounts.map(googleAccount => (
                        <article className={styles.connectionCard} key={googleAccount._id}>
                            <div className={styles.profileImage}>
                                {googleAccount.picture_url ? <ImageWithFallback src={googleAccount.picture_url} alt={`Foto de ${googleAccount.name || "conta Google"}`} functionError={() => <GoogleBrandIcon />} /> : <GoogleBrandIcon />}
                            </div>
                            <div className={styles.connectionIdentity}>
                                <strong>{googleAccount.details_visible ? (googleAccount.name || "Conta Google") : "Credencial Google Ads"}</strong>
                                <span>{googleAccount.details_visible ? (googleAccount.email || "E-mail não informado") : `Conectada por ${googleAccount.connected_by?.name || "usuário removido"}`}</span>
                                <small>{googleAccount.details_visible && `Conectada por ${googleAccount.connected_by?.name || "usuário removido"} · `}Autorizada em {formatDate(googleAccount.last_authorized_at)}</small>
                            </div>
                            <div className={styles.connectionMeta}>
                                <span className={`${styles.badge} ${googleAccount.has_required_scopes ? styles.successBadge : styles.warningBadge}`}>{googleAccount.has_required_scopes ? <MdCheckCircle /> : <MdWarningAmber />}{googleAccount.has_required_scopes ? "Conectada" : "Reconectar"}</span>
                                <span>{accountAdsCount(googleAccount._id)} conta(s) Ads</span>
                            </div>
                            <div className={styles.connectionActions}>
                                {!googleAccount.has_required_scopes && <Button fullWidth={false} onClick={startGoogleAuthorization}>Renovar acesso</Button>}
                                {googleAccount.can_manage_accounts && <Button variant="secondary" fullWidth={false} onClick={() => setManagingConnection(googleAccount)}><MdChecklist /> Gerenciar contas</Button>}
                                {googleAccount.can_manage_accounts && <Button variant="neutral" fullWidth={false} onClick={() => openDisconnect(googleAccount)}><AiOutlineDisconnect /> Desconectar</Button>}
                            </div>
                        </article>
                    ))}
                    {!sortedGoogleAccounts.length && <div className={styles.emptyState}><GoogleBrandIcon /><strong>Nenhuma conta conectada</strong><p>Autorize uma conta Google para descobrir as contas Google Ads às quais ela possui acesso.</p><Button fullWidth={false} disabled={!authorizationUrl} onClick={startGoogleAuthorization}>Conectar conta Google</Button></div>}
                </div>
            </section>

            <section className={styles.card}>
                <div className={styles.sectionHeadingRow}>
                    <div className={styles.sectionHeading}><div><span className={styles.eyebrow}>Recursos encontrados</span><h2>Contas Google Ads</h2><p>Contas acessíveis pelas conexões acima. IDs são exibidos sem formatação para facilitar diagnósticos.</p></div></div>
                    {googleAccounts.some(account => account.can_manage_accounts) && <Button variant="secondary" fullWidth={false} disabled={Boolean(loadingConnection)} onClick={() => syncAdAccounts()}><MdRefresh /> Sincronizar todas</Button>}
                </div>
                <div className={styles.adsList}>
                    {sortedAdAccounts.map(adAccount => (
                        <article key={adAccount._id}>
                            <span className={styles.adsIcon}>{adAccount.manager ? <MdAccountTree /> : <MdAdsClick />}</span>
                            <div><strong>{adAccount.descriptive_name || "Conta Google Ads"}</strong><span>ID {adAccount.customer_id}{adAccount.currency_code ? ` · ${adAccount.currency_code}` : ""}{adAccount.time_zone ? ` · ${adAccount.time_zone}` : ""}</span></div>
                            <span className={`${styles.badge} ${adAccount.manager ? styles.managerBadge : ""}`}>{adAccount.manager ? "MCC" : "Conta de anúncios"}</span>
                        </article>
                    ))}
                    {!sortedAdAccounts.length && <div className={styles.emptyStateCompact}><MdAdsClick /><div><strong>Nenhuma conta Google Ads sincronizada</strong><p>Conecte uma conta Google ou sincronize novamente depois de receber acesso a uma conta Ads.</p></div></div>}
                </div>
            </section>

            {showDisconnect && <ModalConfirm closeModal={() => setShowDisconnect(false)} icon={<AiOutlineDisconnect />} title="Desconectar conta Google?" description={`${accountToDisconnect.name || accountToDisconnect.email || "Esta conta"} deixará de estar disponível nesta organização.`} text="As contas Ads vinculadas serão desativadas. Se esta credencial não estiver sendo usada por outra organização, a Oktto também revogará o token no Google e removerá os dados de autenticação armazenados." onSubmit={() => disconnectAccount(accountToDisconnect)} />}
            {managingConnection && <ModalGoogleAdAccountSelection connection={managingConnection} closeModal={() => setManagingConnection(null)} onSaved={async () => { await Promise.all([loadAccounts(), loadAdAccounts()]); toast.success("Lista de contas autorizadas atualizada.") }} />}
            {isLoading && <Loading fullPage={true} />}
        </div>
    )
}

export default GoogleIntegration
